// The state document holds ISO timestamps, and both scenario run pages show the same three things about them:
// how long something took, when it happened, and whether it is still going.

export const runDuration = (from?: string, to?: string) => {
    if (!from) {
        return '';
    }

    const milliseconds =
        (to ? new Date(to).getTime() : Date.now()) - new Date(from).getTime();

    if (!isFinite(milliseconds) || milliseconds < 0) {
        return '';
    }

    const seconds = Math.floor(milliseconds / 1000);

    if (seconds < 60) {
        return `${seconds}s`;
    }

    if (seconds < 3600) {
        return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(
            2,
            '0'
        )}s`;
    }

    // A run that takes hours is one that hung; the seconds say nothing there.
    const minutes = Math.floor(seconds / 60);

    return minutes < 24 * 60
        ? `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(
              2,
              '0'
          )}m`
        : `${Math.floor(minutes / (24 * 60))}d ${Math.floor(
              (minutes % (24 * 60)) / 60
          )}h`;
};

export const runMoment = (moment?: string) =>
    moment ? new Date(moment).toLocaleString() : '';

// Without seconds: in a list of runs the minute is enough to tell them apart, and shorter reads faster.
export const runMomentShort = (moment?: string) =>
    moment
        ? new Date(moment).toLocaleString(undefined, {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
          })
        : '';

export const runTime = (moment?: string) =>
    moment ? new Date(moment).toLocaleTimeString() : '';

// A run is only over once the runner has written a finish time. Everything else - the status, the activity - can
// still change, so this is what the pages poll on.
export const runIsFinished = (finishedAtUtc?: string) => !!finishedAtUtc;

// One build and the connector apps that run it, as the runner writes it: "c2e-test-cbrcv, c2e-test-exsnd from ...".
const APPS_ON_BUILD = /^([\w.-]+(?:, [\w.-]+)*) from (.+)$/;

// One app and its deployment, when the runner read the build from Azure: "c2e-test-cbrcv deployed ... from ...".
const APP_DEPLOYED = /^[\w.-]+ (deployed .+)$/;

// The part the names share, up to the last dash in it, so "c2e-test-cbrcv" and "c2e-test-cbsnd" keep "cbrcv" and "cbsnd".
const sharedAppPrefix = (apps: string[]) => {
    if (apps.length < 2) return 0;

    const first = apps[0];
    const shortest = Math.min(...apps.map((app) => app.length)) - 1;
    const length = Array.from(first.slice(0, shortest)).findIndex((char, index) =>
        apps.some((app) => app[index] !== char)
    );

    return first.lastIndexOf('-', (length < 0 ? shortest : length) - 1) + 1;
};

/**
 * The build a run judged, without the names of the function apps: they are the same apps on every run.
 *
 * Only when the apps ran different builds does each build keep the apps on it, by the part of their names that
 * differs. A description in any other shape is shown as it is.
 */
export const runBuildLabel = (buildVersion?: string) => {
    if (!buildVersion) return '';

    const deployed = APP_DEPLOYED.exec(buildVersion);

    if (deployed && !buildVersion.includes('; ')) return deployed[1];

    const builds = buildVersion.split('; ').map((part) => APPS_ON_BUILD.exec(part));

    if (builds.some((build) => !build)) return buildVersion;

    const matched = builds as RegExpExecArray[];

    if (matched.length === 1) return matched[0][2];

    const shared = sharedAppPrefix(matched.flatMap((build) => build[1].split(', ')));

    return matched
        .map((build) => `${build[1].split(', ').map((app) => app.slice(shared)).join(', ')}: ${build[2]}`)
        .join('; ');
};
